import cookieConfig from 'config/cookies/languageCode'
import {GetServerSidePropsContext} from 'next'
import {parse} from 'qs'
import {ParsedUrlQuery} from 'querystring'
import {LANGUAGE_CODE_HEADER} from 'utils/proxy/cacheHeaders'

import {getCookie, setCookie} from '@vio/core/src/_web/utils/cookies'
import {resolveCapabilities} from '@vio/core/src/brand/capabilities/resolveCapabilities'
import {CommerceCapabilityContext} from '@vio/core/src/brand/capabilities/types/domains'
import {Brand} from '@vio/core/src/brand/types'
import {isCodeAllowed} from '@vio/core/src/brand/utils'
import {
  defaultLanguage as DEFAULT_LANGUAGE_CODE,
  enabledLanguages as ENABLED_LANGUAGES
} from '@vio/core/src/localization/services/locale'
import {localeLanguageMapper} from '@vio/core/src/localization/services/mappingLocales'
import {isString} from '@vio/core/src/utils/string'

import {normalizeLanguage, parseLocale} from './locale'

export const getEnabledLanguageCode = (
  languageCode: string | null
): string | null => {
  if (!languageCode) return null

  let normalizedLanguage = normalizeLanguage(languageCode)

  if (!normalizedLanguage) return null

  normalizedLanguage = localeLanguageMapper(normalizedLanguage)

  return ENABLED_LANGUAGES.includes(normalizedLanguage)
    ? normalizedLanguage
    : null
}

const getFromQueryString = (query: ParsedUrlQuery): string | null =>
  getValidLocale(query?.lang)

export const getValidLocale = (
  lang: string | string[] | undefined
): string | null => {
  // The value can be a deeper array
  if (!lang || !isString(lang)) return null

  const locale = parseLocale(lang)

  return getEnabledLanguageCode(locale?.language) || null
}

const getFromQueryStringIfForced = (query: ParsedUrlQuery): string | null => {
  if (query?.forceLanguageChange === '1') return getFromQueryString(query)

  return null
}

const getFromPath = (params?: ParsedUrlQuery): string | null => {
  if (params?.locale) return getValidLocale(params.locale)

  return null
}

const getFromCookie = (
  req: GetServerSidePropsContext['req']
): string | null => {
  const languageCode = req.cookies[cookieConfig.name]
  return getEnabledLanguageCode(languageCode) || null
}

const getFromProxyHeader = (
  req: GetServerSidePropsContext['req']
): string | null => getValidLocale(req.headers[LANGUAGE_CODE_HEADER])

const getFromBrowserLanguage = (
  req: GetServerSidePropsContext['req']
): string | null => {
  const acceptLanguageHeader = req.headers['accept-language']
  if (!acceptLanguageHeader || typeof acceptLanguageHeader !== 'string') {
    return null
  }

  // Remove whitespace and split by comma
  const languages = acceptLanguageHeader.trim().split(',')

  if (languages.length === 0) {
    return null
  }

  // Get the first language entry
  const firstLanguage = languages[0].trim()

  if (!firstLanguage) {
    return null
  }

  // Split by semicolon to remove quality value (e.g., "en-US;q=0.9" -> "en-US")
  const languageCode = firstLanguage.split(';')[0].trim()

  const localeData = parseLocale(languageCode)

  return getEnabledLanguageCode(localeData?.language)
}

const getFromBrand = (brand: Brand): string | null => {
  const {bookOnBrand} = resolveCapabilities(
    brand.capabilities,
    {} as CommerceCapabilityContext,
    {skipClientOnly: true}
  ).commerce
  return bookOnBrand.enabled && bookOnBrand.config.defaultLanguageCode
    ? getEnabledLanguageCode(bookOnBrand.config.defaultLanguageCode)
    : null
}

/**
 * Language logic steps:
 *
 * 1 - Path parameter: check if the route carries a locale segment.
 * 2 - Forced query string parameter: check if forceLanguageChange parameter is set to "1" in the URL and use step 4.
 * 3 - Cookie: check if language code is already stored in a cookie and use its value.
 * 4 - Proxy header: use the language set in proxy.ts
 * 5 - Query string parameter: check if lang parameter is present in the URL and use its value.
 * 6 - Brand: get language code from brand config.
 * 7 - Browser language: use the language of the user's browser.
 *
 * Codes outside `brand.languageCodes` are skipped, so a limited brand falls back to its default.
 * @param context - The server context
 * @returns The language code
 */
export const languageCodeFromServerContext = (
  brand: Brand,
  {params, query, req}: GetServerSidePropsContext
): string => {
  const allowed = (code: string | null) =>
    code && isCodeAllowed(code, brand.languageCodes) ? code : null

  return (
    allowed(getFromPath(params)) ||
    allowed(getFromQueryStringIfForced(query)) ||
    allowed(getFromCookie(req)) ||
    allowed(getFromProxyHeader(req)) ||
    allowed(getFromQueryString(query)) ||
    getFromBrand(brand) ||
    allowed(getFromBrowserLanguage(req)) ||
    DEFAULT_LANGUAGE_CODE
  )
}

export const persistLanguageCode = (languageCode: string) => {
  const {secure, sameSite, maxAge: expires} = cookieConfig.options
  setCookie(cookieConfig.name, languageCode, {
    expires,
    secure,
    sameSite
  })
}

const getFromClientQueryString = () => {
  const {lang} = parse(location.search, {
    ignoreQueryPrefix: true
  })

  if (!lang) return null

  // Check if lang url param is array and take the last item
  // Initially required by NFH -> V3 campaigns migration A/B test
  // FIXME: decide if we still need it after A/B is finished
  const language = Array.isArray(lang) ? lang[lang.length - 1] : lang

  // The value can be a deeper array
  if (!isString(language)) return null

  return getEnabledLanguageCode(language)
}

const getFromClientCookie = () => getCookie(cookieConfig.name) || null

const getFromClientBrowserLanguage = () => {
  if (!navigator.language) return null

  const localeData = parseLocale(navigator.language)

  if (!localeData) return null

  return getEnabledLanguageCode(localeData.language)
}

export const getLanguageTrackingInfo = () => {
  return {
    urlLang: getFromClientQueryString(),
    cookieLang: getFromClientCookie(),
    browserLang: getFromClientBrowserLanguage()
  }
}
